import React, { useState } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import TaskCard from './TaskCard'
import TaskForm from './TaskForm'

const categoryColors = {
  todo: 'bg-slate-400',
  in_progress: 'bg-blue-500',
  done: 'bg-green-500',
}

// Sortable wrapper for a task card
const SortableTaskCard = ({ task, onTaskClick, projectKey }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `task-${task.id}`,
    data: { type: 'task', task }
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <TaskCard
        task={task}
        onClick={() => !isDragging && onTaskClick(task)}
        dragHandleProps={listeners}
        isDragging={isDragging}
        projectKey={projectKey}
      />
    </div>
  )
}

const KanbanColumn = ({ status, tasks, onTaskClick, onQuickAdd, addingLoading, projectKey }) => {
  const [showForm, setShowForm] = useState(false)

  const { setNodeRef, isOver } = useDroppable({
    id: `column-${status.id}`,
    data: { type: 'column', statusId: status.id }
  })

  const taskIds = tasks.map(t => `task-${t.id}`)

  const handleQuickAdd = async (title, typeId) => {
    await onQuickAdd(status.id, title, typeId)
    setShowForm(false)
  }

  return (
    <div className="flex flex-col w-72 shrink-0">
      {/* Column header */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className={cn('w-2 h-2 rounded-full shrink-0', categoryColors[status.category] || 'bg-gray-400')} />
        <span className="font-medium text-sm">{status.name}</span>
        <span className="text-xs text-muted-foreground bg-muted rounded-full px-1.5 py-0.5 font-medium">
          {tasks.length}
        </span>
      </div>

      {/* Cards drop zone */}
      <div
        ref={setNodeRef}
        className={cn(
          'flex-1 space-y-2 min-h-[80px] rounded-lg p-2 transition-colors',
          isOver ? 'bg-primary/5 ring-2 ring-primary/20' : 'bg-muted/30'
        )}
      >
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          {tasks.map(task => (
            <SortableTaskCard
              key={task.id}
              task={task}
              onTaskClick={onTaskClick}
              projectKey={projectKey}
            />
          ))}
          {tasks.length === 0 && !isOver && (
            <div className="flex items-center justify-center h-10 text-xs text-muted-foreground/50 select-none">
              No tasks
            </div>
          )}
        </SortableContext>

        {/* Quick add form */}
        {showForm && (
          <TaskForm
            onSubmit={handleQuickAdd}
            onCancel={() => setShowForm(false)}
            loading={addingLoading}
          />
        )}
      </div>

      {/* Add task button */}
      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="mt-2 w-full border-2 border-dashed border-muted-foreground/20 rounded-lg p-2.5 flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add task
        </button>
      )}
    </div>
  )
}

export default KanbanColumn
